import { useState, useEffect } from 'react';
import Moment from 'moment';
import { Stack, TextField } from '@fluentui/react';
import Select from 'react-dropdown-select';
import CountryPrefixes from '../../utils/CountryPrefixes';
import { UserIdentityType } from '../../services/UserIdentitiesService';
import { UserType } from '../../services/UserService';
import DatePickerComponent from '../Elements/DatePickerComponent/DatePickerComponent';
   
const stackTokens = { childrenGap: 50 };
const columnProps = {
    tokens: { childrenGap: 15 },
    styles: { root: { width: 432 } },
};

const regExp = RegExp(/^[a-zA-Z0-9]+@[a-zA-Z0-9]+\.[A-Za-z]+$/);

type TUserFormProps = {
    user: UserType
    setIsButtonDisabled: (value: boolean) => void
    setUserIdentity: (value: UserIdentityType) => void
}

const UserForm = (props: TUserFormProps) => {
    var country = CountryPrefixes.find((country) => country.phone_code === (props.user !== undefined ? props.user.mainUserIdentity?.countryPrefix : '+34'));

    const userInitialState: UserIdentityType  = {
        firstName: props.user.mainUserIdentity?.firstName !== undefined ? props.user.mainUserIdentity.firstName : '',
        lastName: props.user.mainUserIdentity?.lastName || '',
        secondLastName: props.user.mainUserIdentity?.secondLastName || '',
        birthday: props.user.mainUserIdentity?.birthday ? new Date(props.user.mainUserIdentity.birthday) : undefined,
        email: props.user.email,
        countryPrefix: country !== undefined ? country.phone_code : '+34',
        phone: props.user.mainUserIdentity?.phone || '',
        status: props.user.mainUserIdentity?.status
    }

    const [userIdentity, setUserIdentity] = useState<UserIdentityType>(userInitialState);

    const [errors, setErrors] = useState<any>({});

    const formDateChange = (value: Date | null | undefined) => {
        if(value === null) {
            setErrors((prevState: any) => ({
                ...prevState,
                birthday: "La fecha de nacimiento no puede estar vacía"
            }));
            return;
        }
        else {
            var currentDate = Moment();
            var birthdayDate = Moment(value);
            if(currentDate.diff(birthdayDate, 'years') < 18) {
                setErrors((prevState: any) => ({
                    ...prevState,
                    birthday: "El titular principal debe ser mayor de edad"
                }));
            }
            else {
                // @ts-ignore
                delete errors.birthday;
                setErrors(() => ({
                    ...errors
                }));

                setUserIdentity(prevState => ({
                    ...prevState,
                    birthday: value || new Date()
                }));
            }
        }
    };

    const formValChange = (e: any) => {
        const name:string = e.target.name;
        const value:string = e.target.value;

        switch(name) {
            case "firstName":
            case "lastName":
            case "secondLastName":
                if(value.length < 3) {
                    setErrors((prevState: any) => ({
                        ...prevState,
                        [name]: "Debe contener al menos 2 caracteres"
                    }));
                }
                else {
                    // @ts-ignore
                    delete errors[name];
                    setErrors(() => ({
                        ...errors
                    }));
                }
                break;

            case "email":
                if(regExp.test(value)) {
                    setErrors((prevState: any) => ({
                        ...prevState,
                        email: "Formato no válido"
                    }));
                }
                else {
                    // @ts-ignore
                    delete errors.email;
                    setErrors(() => ({
                        ...errors
                    }));
                }
                break;

            case "countryPrefix":
                if(value.length === 0) {
                    setErrors((prevState: any) => ({
                        ...prevState,
                        countryCode: "Debes seleccionar un prefijo"
                    }));
                }
                else {
                    // @ts-ignore
                    delete errors.countryCode;
                    setErrors(() => ({
                        ...errors
                    }));
                }
                break;

            case "phone":
                if(value.length < 9) {
                    setErrors((prevState: any) => ({
                        ...prevState,
                        phone: "Debe contener al menos 9 dígitos"
                    }));
                }
                else {
                    // @ts-ignore
                    delete errors.phone;
                    setErrors(() => ({
                        ...errors
                    }));
                }
                break;

            default:
                break;
        }

        setUserIdentity(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    useEffect(() => {
        var someEmptyField = Object.values(userIdentity).some((value) => { return value === "" });
        var someFieldError = Object.keys(errors).length > 0;
        props.setIsButtonDisabled(someEmptyField || someFieldError);

        if(!someEmptyField || !someFieldError) {
            props.setUserIdentity(userIdentity);
        }
    }, [errors]);

    return (
        <form onSubmit={() => formValChange} noValidate>
            <Stack tokens={stackTokens}>
                <Stack horizontal {...columnProps}>
                    <Stack.Item grow>
                        <TextField
                            label="Nombre"
                            required
                            placeholder="Ingresa el nombre"
                            name="firstName"
                            value={userIdentity.firstName}
                            onChange={formValChange}
                            errorMessage={'firstName' in errors ? errors['firstName'] : undefined}
                            readOnly={!!userInitialState.firstName}
                        />
                    </Stack.Item>
                    <Stack.Item grow>
                        <TextField
                            label="Primer Apellido"
                            required
                            placeholder="Ingresa el primer apellido"
                            name="lastName"
                            value={userIdentity.lastName}
                            onChange={formValChange}
                            errorMessage={'lastName' in errors ? errors.lastName : undefined}
                            readOnly={!!userInitialState.lastName}
                        />
                    </Stack.Item>
                </Stack>
                <Stack horizontal {...columnProps}>
                    <Stack.Item grow>
                        <TextField
                            label="Segundo Apellido"
                            required
                            placeholder="Ingresa el segundo apellido"
                            name="secondLastName"
                            value={userIdentity.secondLastName}
                            onChange={formValChange}
                            errorMessage={'secondLastName' in errors ? errors.secondLastName : undefined}
                            readOnly={!!userInitialState.secondLastName}
                        />
                    </Stack.Item>
                    <Stack.Item grow>
                            <DatePickerComponent userIdentity={userIdentity} userInitial={userInitialState} formDateChange={formDateChange} />
                    </Stack.Item>
                </Stack>
                <Stack {...columnProps}>
                    <TextField
                        label="Correo electrónico"
                        required
                        placeholder="Ingresa el correo electrónico"
                        name="email"
                        value={userIdentity.email}
                        onChange={formValChange}
                        errorMessage={'email' in errors ? errors.email : undefined}
                        readOnly={!!userInitialState.email}
                    />
                </Stack>
                <Stack horizontal {...columnProps}>
                    <Stack.Item>
                        <label className="react-dropdown-select-label">Prefijo (país) *</label>
                        <Select
                            labelField='name'
                            name="countryCode"
                            options={CountryPrefixes}
                            onChange={(value: any) => formValChange({ target: { name: 'countryPrefix', value: value[0].phone_code } }) }
                            dropdownHeight='2000'
                            noDataLabel={'No encontrado'}
                            backspaceDelete={false}
                            values={CountryPrefixes.filter((countryPrefix) => countryPrefix.phone_code === userIdentity.countryPrefix)}
                            placeholder=''
                            itemRenderer={({ item, methods }) => (
                                <span
                                    role="option"
                                    aria-selected="false"
                                    aria-label={item.name + ' ' + item.phone_code}
                                    // @ts-ignore
                                    tabIndex="-1"
                                    className="react-dropdown-select-item"
                                    onClick={() => methods.addItem(item)}
                                    key={'operation-country-prefix-' + item.iso3}
                                >
                                    {item.name + ' ' + item.phone_code}
                                </span>
                            )}
                            searchable={true}
                            searchBy="text"
                            disabled={!!props.user?.mainUserIdentity?.countryPrefix}
                        />
                    </Stack.Item>
                    <Stack.Item grow={3}>
                        <TextField
                            label="Número teléfono móvil"
                            required
                            placeholder="Introduzca el teléfono móvil"
                            name="phone"
                            value={userIdentity.phone}
                            onChange={formValChange}
                            errorMessage={'phone' in errors ? errors.phone : undefined}
                            readOnly={!!userInitialState.phone}
                        />
                    </Stack.Item>
                </Stack>
            </Stack>
        </form>
    );
};

export default UserForm;